import React, { useState, useEffect, useCallback } from 'react';
import { Gate } from '../AccessStudio';

// The day's meal menu for one class. Children are marked against these items in
// their per-child report. Per class, per day. See DAYCARE_DIARY_DESIGN.md.

const DaycareMenuEditorModal = ({ isOpen, onClose, onSuccess, classId, className, date, apiCall, showError, showSuccess }) => {
  const [items, setItems] = useState([]);   // [{ itemName }]
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const MEAL_TYPES = ['Breakfast', 'Lunch', 'Snack', 'Other'];

  const load = useCallback(async () => {
    if (!classId || !date) return;
    setLoading(true);
    try {
      const data = await apiCall(`/daycare/daily-report/menu?classId=${classId}&date=${date}`);
      setItems((data || []).map((m) => ({ mealType: m.MealType || 'Lunch', itemName: m.ItemName })));
    } catch (e) {
      showError('Error loading menu: ' + e.message);
    } finally { setLoading(false); }
  }, [classId, date, apiCall, showError]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const setField = (i, field, v) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [field]: v } : it));
  const addRow = () => setItems((arr) => [...arr, { mealType: 'Lunch', itemName: '' }]);
  const removeRow = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiCall('/daycare/daily-report/menu', {
        method: 'PUT',
        body: JSON.stringify({
          classId, date,
          items: items.map((it, idx) => ({ mealType: it.mealType, itemName: it.itemName, sortOrder: idx * 10 })).filter((it) => it.itemName.trim()),
        }),
      });
      const kept = r && Array.isArray(r.keptReferenced) ? r.keptReferenced : [];
      if (kept.length) {
        showSuccess(`Menu saved. Kept ${kept.join(', ')} — meals were already recorded against ${kept.length === 1 ? 'it' : 'them'}, so ${kept.length === 1 ? 'it' : 'they'} can't be removed.`);
      } else {
        showSuccess('Menu saved');
      }
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      showError('Error saving menu: ' + e.message);
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Menu — {className}</h2>
            <p className="text-emerald-100 text-sm">{date}</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl leading-none">&times;</button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : (
          <div className="p-6 overflow-y-auto space-y-2">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-2">
                <select value={it.mealType} onChange={(e) => setField(i, 'mealType', e.target.value)}
                  className="border border-gray-300 rounded px-2 py-2 text-sm">
                  {MEAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input value={it.itemName} onChange={(e) => setField(i, 'itemName', e.target.value)} placeholder="e.g. moujadara, salade"
                  className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm" />
                <button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700 px-2">✕</button>
              </div>
            ))}
            <button onClick={addRow} className="text-sm text-emerald-700 hover:text-emerald-900 font-medium">+ Add item</button>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <Gate permission="daycare.daily_report.edit" label="Save menu">
            <button onClick={save} disabled={saving || loading} className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:bg-gray-300">{saving ? 'Saving…' : 'Save Menu'}</button>
          </Gate>
        </div>
      </div>
    </div>
  );
};

export default DaycareMenuEditorModal;
